/**
 * The case workbench's reads (vNext-api `docs/workbench.md`).
 *
 * One cheap poll per open workbench: the header, on its ETag. Its version
 * is the case's change token — every case-state write is one case event —
 * so the views' reads don't poll at all: they read when they mount and
 * again whenever the header's version moves.
 *
 * Every read sends its own ETag, kept across mounts (`workbenchReadCache`):
 * a view opened again shows what it last read at once, and a move of the
 * case's version that didn't touch a view costs that view a 304.
 */

import { useRef } from 'react';

import * as Sentry from '@sentry/react';

import type { ConditionalRequestOptions, ConditionalResult } from '@/services/http';
import { vnextApiClient } from '@/services/vnext-api';
import type {
  ChecklistItem,
  EvidenceIndexEntry,
  EvidenceItem,
  EvidencePage,
  EvidenceQuery,
  Intervention,
  ReviewIssue,
  WorkbenchActivity,
  WorkbenchBrief,
  WorkbenchConversations,
  WorkbenchDocument,
  WorkbenchFile,
  WorkbenchHeader,
  WorkbenchPrefill,
  WorkbenchSetup,
} from '@/types/workbench';
import { toError } from '@/utils/errors';
import { UNCHANGED, usePoll } from './usePoll';
import { readCached, writeCached } from './workbenchReadCache';

/** The header's cadence — a 304 in the steady state. */
export const WORKBENCH_POLL_MS = 10_000;

export interface WorkbenchRead<T> {
  data: T | null;
  error: Error | null;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

function errorOf(error: unknown): Error | null {
  return error === undefined ? null : toError(error);
}

type Fetch<T> = (
  reportId: string,
  options: ConditionalRequestOptions
) => Promise<ConditionalResult<T>>;

/**
 * The fetch of a cached read: conditional on the ETag of what's cached (none
 * for a person's own refetch), and what comes back cached. A 304 hands the
 * poll the cached data the first time (a view opened again), and is
 * UNCHANGED after that, so a quiet tick re-renders nothing.
 */
function useCachedFetch<T>(key: string, reportId: string, fetch: Fetch<T>) {
  // The key whose data this hook last handed its poll.
  const delivered = useRef<string | null>(null);
  return async ({
    signal,
    manual,
  }: {
    signal: AbortSignal;
    manual: boolean;
  }): Promise<T | typeof UNCHANGED> => {
    const cached = readCached(key);
    const etag = manual ? null : (cached?.etag ?? null);
    const result = await fetch(reportId, { signal, etag });
    if (result === 'not-modified') {
      if (!cached || delivered.current === key) {
        return UNCHANGED;
      }
      delivered.current = key;
      return cached.data as T;
    }
    writeCached(key, { data: result.data, etag: result.etag });
    delivered.current = key;
    return result.data;
  };
}

/** The case at a glance, polled on its ETag. */
export function useWorkbenchHeader(reportId: string): WorkbenchRead<WorkbenchHeader> {
  const key = `header:${reportId}`;
  const queryFn = useCachedFetch(key, reportId, (id, options) =>
    vnextApiClient.getWorkbenchHeader(id, options)
  );
  const poll = usePoll<WorkbenchHeader>({
    interval: WORKBENCH_POLL_MS,
    refetchDeps: [reportId],
    queryFn,
    onError: (err) => Sentry.captureException(toError(err)),
  });
  const data = poll.data ?? (readCached(key)?.data as WorkbenchHeader | undefined) ?? null;
  return {
    data,
    error: errorOf(poll.error),
    isLoading: data === null && poll.isLoading,
    refetch: poll.refetch,
  };
}

/** A read that follows the case's version: once when it mounts, then on
 * every change of `version` (null until the header has loaded) — and every
 * `interval` too, for what changes without moving it. Until the first read
 * lands, what this view read last time it was open. */
function useCaseRead<T>(
  name: string,
  reportId: string,
  version: string | null,
  fetch: Fetch<T>,
  interval: number | null = null
): WorkbenchRead<T> {
  const key = `${name}:${reportId}`;
  const queryFn = useCachedFetch(key, reportId, fetch);
  const poll = usePoll<T>({
    enabled: version !== null,
    interval,
    refetchDeps: [reportId, version],
    queryFn,
    onError: (err) => Sentry.captureException(toError(err)),
  });
  const data = poll.data ?? (readCached(key)?.data as T | undefined) ?? null;
  return {
    data,
    error: errorOf(poll.error),
    isLoading: data === null && (version === null || poll.isLoading),
    refetch: poll.refetch,
  };
}

/** The case for someone picking it up cold. */
export function useWorkbenchBrief(
  reportId: string,
  version: string | null
): WorkbenchRead<WorkbenchBrief> {
  return useCaseRead('brief', reportId, version, (id, options) =>
    vnextApiClient.getWorkbenchBrief(id, options)
  );
}

/** The checklist as it stands. */
export function useWorkbenchChecklist(
  reportId: string,
  version: string | null
): WorkbenchRead<ChecklistItem[]> {
  return useCaseRead('checklist', reportId, version, (id, options) =>
    vnextApiClient.listWorkbenchChecklist(id, options)
  );
}

/** Every file on the case, with the entries the model wrote. */
export function useWorkbenchFiles(
  reportId: string,
  version: string | null
): WorkbenchRead<WorkbenchFile[]> {
  return useCaseRead('files', reportId, version, (id, options) =>
    vnextApiClient.listWorkbenchFiles(id, options)
  );
}

/** The case's conversations: the client's and the partners'
 * (plans/workbench2/06-chat-tab.md). */
export function useWorkbenchConversations(
  reportId: string,
  version: string | null
): WorkbenchRead<WorkbenchConversations> {
  return useCaseRead('conversations', reportId, version, (id, options) =>
    vnextApiClient.getWorkbenchConversations(id, options)
  );
}

/** The key a page of records is cached under: its filters and its page. */
function evidenceKey(query: EvidenceQuery): string {
  return [
    'evidence',
    query.status ?? '',
    query.subjectId ?? '',
    query.checklistItemId ?? '',
    query.includeSuperseded ? 'all' : '',
    query.page,
    query.pageSize,
  ].join(':');
}

/** A page of the case's records, filtered by the API — read again when the
 * case's version or the query changes; `query` null reads nothing (a
 * check's row not open yet). Until a query's read lands, what it read last
 * time — never another query's page. */
export function useWorkbenchEvidence(
  reportId: string,
  version: string | null,
  query: EvidenceQuery | null
): WorkbenchRead<EvidencePage> {
  const key = `${query ? evidenceKey(query) : 'evidence:none'}:${reportId}`;
  const queryFn = useCachedFetch<{ key: string; page: EvidencePage }>(
    key,
    reportId,
    async (id, options) => {
      if (!query) {
        return 'not-modified';
      }
      const result = await vnextApiClient.listWorkbenchEvidence(id, query, options);
      return result === 'not-modified'
        ? result
        : { data: { key, page: result.data }, etag: result.etag };
    }
  );
  const poll = usePoll<{ key: string; page: EvidencePage }>({
    enabled: version !== null && query !== null,
    interval: null,
    refetchDeps: [reportId, version, key],
    queryFn,
    onError: (err) => Sentry.captureException(toError(err)),
  });
  const read = poll.data?.key === key ? poll.data.page : null;
  const cached = query
    ? (readCached(key)?.data as { page: EvidencePage } | undefined)?.page
    : undefined;
  const data = read ?? cached ?? null;
  return {
    data,
    error: query === null || data !== null ? null : errorOf(poll.error),
    isLoading: query !== null && data === null && (version === null || poll.isLoading),
    refetch: poll.refetch,
  };
}

/** Every record on the case by number: how screens name the records agents
 * cite by id. */
export function useWorkbenchEvidenceIndex(
  reportId: string,
  version: string | null
): WorkbenchRead<EvidenceIndexEntry[]> {
  return useCaseRead('evidence-index', reportId, version, (id, options) =>
    vnextApiClient.getWorkbenchEvidenceIndex(id, options)
  );
}

/**
 * One record by id, whole — a record opened from a chip, an agent's words or
 * an ask: read while `evidenceId` is given, again when the case's version
 * moves.
 */
export function useWorkbenchEvidenceItem(
  reportId: string,
  evidenceId: string | null,
  version: string | null
): WorkbenchRead<EvidenceItem> {
  const key = `evidence-item:${evidenceId ?? ''}:${reportId}`;
  const queryFn = useCachedFetch(key, reportId, (id, options) =>
    vnextApiClient.getWorkbenchEvidenceItem(id, evidenceId ?? '', options)
  );
  const poll = usePoll<EvidenceItem>({
    enabled: version !== null && evidenceId !== null,
    interval: null,
    refetchDeps: [reportId, evidenceId, version],
    queryFn,
    onError: (err) => Sentry.captureException(toError(err)),
  });
  const read = poll.data ?? (readCached(key)?.data as EvidenceItem | undefined) ?? null;
  // Until this record's read lands, the poll may still hold the last one's.
  const data = read !== null && read.id === evidenceId ? read : null;
  return {
    data,
    error: evidenceId === null || data !== null ? null : errorOf(poll.error),
    isLoading: evidenceId !== null && data === null && (version === null || poll.isLoading),
    refetch: poll.refetch,
  };
}

/** The case's asks: open, answered and cancelled. */
export function useWorkbenchInterventions(
  reportId: string,
  version: string | null
): WorkbenchRead<Intervention[]> {
  return useCaseRead('interventions', reportId, version, (id, options) =>
    vnextApiClient.listWorkbenchInterventions(id, options)
  );
}

export function useWorkbenchActivity(
  reportId: string,
  version: string | null
): WorkbenchRead<WorkbenchActivity> {
  return useCaseRead('activity', reportId, version, async (id, options) => ({
    data: await vnextApiClient.getWorkbenchActivity(id, options),
    etag: null,
  }));
}

/** The report's versions, the workbench's Word drafts and the partners'
 * returns, newest first. */
export function useWorkbenchDocuments(
  reportId: string,
  version: string | null
): WorkbenchRead<WorkbenchDocument[]> {
  return useCaseRead('documents', reportId, version, (id, options) =>
    vnextApiClient.listWorkbenchDocuments(id, options)
  );
}

/** The partner's form as the workbench pre-filled it: the case's own and
 * each assignment's copy. */
export function useWorkbenchPrefills(
  reportId: string,
  version: string | null
): WorkbenchRead<WorkbenchPrefill[]> {
  return useCaseRead('prefills', reportId, version, (id, options) =>
    vnextApiClient.listWorkbenchPrefills(id, options)
  );
}

/** What the reviewer found in the case's documents. */
export function useWorkbenchReviewIssues(
  reportId: string,
  version: string | null
): WorkbenchRead<ReviewIssue[]> {
  return useCaseRead('review-issues', reportId, version, (id, options) =>
    vnextApiClient.listWorkbenchReviewIssues(id, options)
  );
}

/** The setup is read again this often while it's open: the request's
 * triage and its partner assignments move on their own clocks (its ETag
 * answers 304 in between). */
export const SETUP_POLL_MS = 15_000;

/** The case's setup: the request and its files, the subjects, the report
 * type, the checklist, partner work, the price, the case agent's proposed
 * values, and what Start still needs. */
export function useWorkbenchSetup(
  reportId: string,
  version: string | null
): WorkbenchRead<WorkbenchSetup> {
  return useCaseRead(
    'setup',
    reportId,
    version,
    (id, options) => vnextApiClient.getWorkbenchSetup(id, options),
    SETUP_POLL_MS
  );
}
